/* Advanced — `tt_settings` as it actually is.
 *
 * Every other page in this app is a curated view of these rows: a switch, a
 * colour picker, a week of times. This one is the table. It exists so that
 * nothing about a salon is unreachable because a control for it has not been
 * built yet — which is a promise the curated pages cannot keep on their own,
 * because a setting added to a newer console is a setting this build has never
 * heard of.
 *
 * Three things it does that no other page does:
 *
 *   shows unknown keys   a row this build does not recognise is listed, marked,
 *                        and editable. The console ignores it — `hydrate` drops
 *                        anything not in its DEFAULTS — but it is in the table
 *                        and a newer build will read it.
 *   writes without a net anything valid JSON goes in exactly as typed. The
 *                        curated pages run values through js/rules/settings-keys
 *                        first; this one does not, deliberately. It is the
 *                        escape hatch, and an escape hatch that validates is a
 *                        form.
 *   deletes a row        which is not the same as writing the default into it.
 *                        See `clearSetting` in js/store.js.
 *
 * The console is more robust to nonsense here than it looks — the theme loader
 * validates the preset and every custom hex, and an unreadable value falls back
 * rather than throwing. That is a reason to be unafraid of this page, not a
 * reason to be careless with it: a plausible-but-wrong number goes straight
 * through.
 */
import * as Vue from 'vue';
import { api } from '../api.js';
import { store } from '../store.js';
import { nav } from '../nav.js';
import { settingKeys } from '../rules/settings-keys.js';
import { JsonEditor } from '../components/json-editor.js';
import { ConfirmButton } from '../components/confirm-button.js';

var Advanced = {
  name: 'AdminAdvanced',
  components: { JsonEditor: JsonEditor, ConfirmButton: ConfirmButton },
  setup: function () {
    var open = Vue.reactive({});
    var newKey = Vue.ref('');
    var newValue = Vue.ref('true');
    var showUnsetOnly = Vue.ref(false);

    var inventory = Vue.computed(function () {
      var list = settingKeys.inventory(store.data.settings);
      return showUnsetOnly.value
        ? list.filter(function (r) { return !r.present; })
        : list;
    });

    var unknownCount = Vue.computed(function () {
      return settingKeys.inventory(store.data.settings)
        .filter(function (r) { return !r.known; }).length;
    });

    function describe(row) { return settingKeys.describe(row.key, row.value); }

    /* Straight to the table, with no cleaning of any kind.
     *
     * `store.setSetting` is not used here on purpose — it validates against the
     * registry, which is exactly what this page is for going around. What it
     * does keep is the tenant stamp, because that is not a validation rule, it
     * is the difference between editing this salon and editing another one.
     */
    async function write(key, value) {
      var id = store.selectedId.value;
      var row = { key: key, value: value, updated_at: new Date().toISOString() };
      var res = await api.writeRow('tt_settings', id, row, 'tenant_id,key');
      if (!res) return;

      var existing = store.settingRow(key);
      if (existing) existing.value = value;
      else store.data.settings.push(Object.assign({ tenant_id: id }, row));

      api.log(id, 'setting.raw', { key: key, value: value });
      api.toast('Wrote ' + key + '.');
    }

    async function add() {
      var key = newKey.value.trim();
      if (!key) { api.toast('A key needs a name.', 'bad'); return; }
      var value;
      try {
        value = newValue.value.trim() === '' ? null : JSON.parse(newValue.value);
      } catch (e) {
        api.toast('That value is not JSON.', 'bad');
        return;
      }
      await write(key, value);
      newKey.value = '';
      newValue.value = 'true';
      open[key] = true;
    }

    function toggle(key) { open[key] = !open[key]; }

    return {
      inventory: inventory,
      unknownCount: unknownCount,
      open: open,
      toggle: toggle,
      describe: describe,
      write: write,
      clear: store.clearSetting,
      newKey: newKey,
      newValue: newValue,
      add: add,
      showUnsetOnly: showUnsetOnly,
      reload: function () { store.loadTenant(store.selectedId.value, true); },
      stamp: store.stamp,
      goPage: nav.goPage
    };
  },
  template: [
    '<div class="page">',
    '  <div class="page__head">',
    '    <h2>Advanced</h2>',
    '    <p class="page__lead">The salon&rsquo;s <code>tt_settings</code> rows, as they are.',
    '      Nothing typed here is validated &mdash; if it is JSON, it is written. This is the',
    '      way to reach a setting no page has a control for.</p>',
    '  </div>',
    '',
    '  <div class="card card--warn">',
    '    <h3>Before you edit</h3>',
    '    <ul class="notes">',
    '      <li>Every station reads these within a second or two, and the change is live on a',
    '        salon floor.</li>',
    '      <li><strong>Not set</strong> is a real state and not the same as the default value',
    '        written out. Deleting a row is how a salon goes back to a fresh install.</li>',
    '      <li>A key this build does not know is not broken. The console ignores what it does',
    '        not recognise rather than failing on it &mdash; a newer build may read it.</li>',
    '    </ul>',
    '  </div>',
    '',
    '  <div class="card">',
    '    <div class="card__row">',
    '      <label class="switch">',
    '        <input type="checkbox" v-model="showUnsetOnly" />',
    '        <span class="switch__track"><span class="switch__thumb"></span></span>',
    '        <span class="switch__label">Only keys with no row</span>',
    '      </label>',
    '      <span class="hint hint--inline" v-if="unknownCount">',
    '        {{ unknownCount }} key{{ unknownCount === 1 ? "" : "s" }} this build does not know.</span>',
    '      <button type="button" class="btn btn--ghost btn--sm" @click="reload()">Reload</button>',
    '    </div>',
    '  </div>',
    '',
    '  <ul class="rows">',
    '    <li v-for="row in inventory" :key="row.key" class="row row--stack"',
    '      :class="{ \'is-unknown\': !row.known, \'is-unset\': !row.present }">',
    '      <button type="button" class="row__head" @click="toggle(row.key)">',
    '        <span class="row__key mono">{{ row.key }}</span>',
    '        <span class="row__desc">{{ describe(row) }}</span>',
    '        <span v-if="!row.known" class="pill pill--warn">unknown key</span>',
    '        <span v-else-if="!row.present" class="pill">no row</span>',
    '        <span class="row__edit">{{ open[row.key] ? "Close" : "Edit" }}</span>',
    '      </button>',
    '',
    '      <div v-if="open[row.key]" class="row__body">',
    '        <p class="hint">{{ row.hint }}</p>',
    '        <p v-if="row.updatedAt" class="hint">Last written {{ stamp(row.updatedAt) }}.</p>',
    '        <json-editor :value="row.value" :save-label="\'Write \' + row.key"',
    '          @save="write(row.key, $event)" />',
    '        <div class="card__row">',
    '          <confirm-button v-if="row.present" label="Delete this row" size="sm"',
    '            confirm="Delete — back to the shipped default"',
    '            @confirmed="clear(row.key)" />',
    '        </div>',
    '      </div>',
    '    </li>',
    '  </ul>',
    '',
    '  <form class="card" @submit.prevent="add">',
    '    <h3>Add a key</h3>',
    '    <p class="hint">For a setting a newer console reads and this build has never heard of.',
    '      The name is stored exactly as typed &mdash; the console matches it literally.</p>',
    '    <div class="card__row">',
    '      <label class="field field--grow">',
    '        <span class="field__label">Key</span>',
    '        <input v-model="newKey" class="input mono" type="text" spellcheck="false"',
    '          placeholder="someNewSetting" />',
    '      </label>',
    '      <label class="field field--grow">',
    '        <span class="field__label">Value (JSON)</span>',
    '        <input v-model="newValue" class="input mono" type="text" spellcheck="false"',
    '          placeholder="true" />',
    '      </label>',
    '      <button type="submit" class="btn btn--primary">Write</button>',
    '    </div>',
    '  </form>',
    '</div>'
  ].join('\n')
};

nav.register({
  id: 'advanced',
  name: 'Advanced',
  group: 'Advanced',
  page: Advanced
});

export { Advanced };
